/**
 * The reason an Uber call failed, in words worth showing an operator.
 *
 * Replaces five near-identical copies of this logic. They all collapsed several
 * very different failures into one generic sentence, so "Failed to re-book the
 * trip." could mean the server refused it, the server never answered, or the
 * answer could not be decrypted - three things with three different fixes.
 *
 * The server sends its own wording as { error } or { message }; anything else
 * here is the client saying what went wrong instead.
 */
export function readApiError(err, fallback) {
  // Always leave the raw failure in the console. The toast has one line; the
  // person debugging needs the status, the URL and the body.
  if (err) console.error('[Uber API]', err);

  const response = err && err.response;

  // No response at all - the request never completed. A wrong message here
  // sends people looking at the wrong end, so name it for what it is.
  if (!response) {
    return 'Could not reach the server. Check your connection and that the API is running.';
  }

  const data = response.data;

  if (typeof data === 'string' && data.trim()) return data;

  // `message` before `error`: where an endpoint sends both, `message` is the
  // sentence written for a person and `error` is a short code. Reading `error`
  // first showed "Blocked" in place of "The crew number is not a mobile phone."
  if (data && typeof data.message === 'string' && data.message) return data.message;
  if (data && typeof data.error === 'string' && data.error) return data.error;

  // Still the encrypted envelope: the response arrived but could not be read,
  // so the server's own wording is in there and unreachable.
  if (data && typeof data.data === 'string') {
    return `The server rejected this (${response.status}) but its reply could not be read.`;
  }

  return `${fallback} (${response.status})`;
}

export default readApiError;
